import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { OrderStore } from '../services/order-store';
import { Snackbar } from '../services/snackbar';

/**
 * Booking-flow guards. Each step can only be reached once its prerequisites
 * exist in the OrderStore; otherwise the user is sent to the first incomplete
 * step with a short explanation. This stops the flow being skipped via the
 * cart shortcut or a pasted URL (and prevents paying with incomplete details).
 */

/** Item details (step 1): a service type must be chosen first. */
export const itemDetailsGuard: CanActivateFn = () => {
  const store = inject(OrderStore);
  const router = inject(Router);
  const snackbar = inject(Snackbar);

  if (!store.hasServiceType()) {
    snackbar.info('Choose a service to start your booking.');
    router.navigate(['/']);
    return false;
  }
  return true;
};

/** Collection (step 2): needs a service type and at least one item. */
export const collectionGuard: CanActivateFn = () => {
  const store = inject(OrderStore);
  const router = inject(Router);
  const snackbar = inject(Snackbar);

  if (!store.hasServiceType()) {
    snackbar.info('Choose a service to start your booking.');
    router.navigate(['/']);
    return false;
  }
  if (!store.hasItems()) {
    snackbar.info('Add at least one item before entering collection details.');
    router.navigate(['/delivery-request']);
    return false;
  }
  return true;
};

/** Payment (step 4): every earlier step must be complete. */
export const checkoutGuard: CanActivateFn = () => {
  const store = inject(OrderStore);
  const router = inject(Router);
  const snackbar = inject(Snackbar);

  if (!store.hasServiceType()) {
    snackbar.info('Choose a service to start your booking.');
    router.navigate(['/']);
    return false;
  }
  if (!store.hasItems()) {
    snackbar.info('Add at least one item before checking out.');
    router.navigate(['/delivery-request']);
    return false;
  }
  if (!store.isCollectionComplete()) {
    snackbar.info('Complete your collection details before checking out.');
    router.navigate(['/address-details']);
    return false;
  }
  return true;
};
